import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const spoofingSafelyGuide: AdminGuide = {
  id: "spoofing-safely",
  title: "View as a customer safely",
  category: "Access & Security",
  audience: ["Support", "Ops", "CS"],
  summary:
    "View the product as a customer to reproduce an issue without leaking credentials or changing customer data by accident.",
  keywords: [
    "spoof",
    "impersonation",
    "login as",
    "support",
    "reproduce",
    "token",
    "incognito",
    "audit",
    "permissions",
  ],
  location: "Admin",
  risk: "high",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.AGENT_BROWSER,
    GUIDE_SOURCE_DOCS.USAGE_TRACKING,
    GUIDE_SOURCE_DOCS.ADMIN_API_DOCS,
  ],
  warnings: [
    "High risk: Only spoof with explicit customer permission and a clear internal reason (ticket/case).",
    "Treat tokenised login URLs (anything containing `token=`) as credentials. Do not paste them into Slack, email, tickets, bug reports, screenshots, or docs.",
    "Avoid making changes in a spoofed session. If you must change something, do the smallest change and record exactly what you changed.",
  ],
  safetyNotes: [
    "Prefer an Incognito/Private window so you do not mix your own cookies/session with the customer session.",
    "Do not share screenshots that contain customer financial data unless you have permission and you crop to the minimum area.",
    "If you are unsure you are in the correct organisation or user account: stop. Do not continue.",
  ],
  whatThisHelpsYouDo: [
    "View the customer experience as the selected user.",
    "Reproduce a reported issue using the same routes and data the customer sees.",
    "Capture high-signal evidence (steps, expected vs actual, timestamps).",
  ],
  whenToUse: [
    "A customer has explicitly consented and you need to reproduce their issue.",
    "You need to confirm whether an issue is user-specific, org-specific, or environment-specific.",
    "Engineering asks you to validate a fix in the real customer context.",
  ],
  whenNotToUse: [
    "You are “just browsing” accounts without a clear reason.",
    "You need to perform billing, provider connection, or other high-impact changes (use the dedicated guides or escalate).",
    "You cannot confirm you have the correct customer identifiers.",
  ],
  beforeYouStart: [
    "Customer permission is recorded (ticket/case/email).",
    "You have the customer identifier(s): user email and/or user UID.",
    "You know which environment to use (Preview vs Production).",
    "You are ready to work in an Incognito/Private window.",
  ],
  steps: [
    "Confirm permission and your purpose. Write down the ticket/case reference.",
    "Open Admin → **View as customer** (`/spoof`).",
    "Search by name or email and select the correct user.",
    "Double-check you selected the correct person (email/UID). If anything looks off, stop.",
    "Create the spoof session and open it in an Incognito/Private window.",
    "In the customer app, confirm you are in the intended organisation (check the organisation name in the UI).",
    "Reproduce the issue once. Then refresh and reproduce again to confirm it is consistent.",
    "Capture evidence: exact steps, expected vs actual, and the time (include timezone).",
    "End the spoof session by logging out of the customer app and closing the spoof-session window.",
    "Record what you changed (ideally: “no changes”). If you changed something, list it explicitly.",
  ],
  checklists: [
    {
      title: "Before you start a spoof session",
      items: [
        "Permission is recorded and linked to a ticket/case.",
        "Correct user identified (email/UID).",
        "Correct organisation identified (name and, if possible, organisationId).",
        "You will use an Incognito/Private window (no cookie mixing).",
      ],
    },
    {
      title: "After you finish the spoof session",
      items: [
        "You logged out and closed the spoof window.",
        "You did not paste any tokenised URLs into tickets, Slack, or bug reports.",
        "You recorded what you changed (ideally: nothing).",
        "You captured timestamps and enough steps for Engineering to reproduce.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "You confirmed whether the issue reproduces with clear steps.",
    "You captured timestamps and environment (Preview/Prod).",
    "You exited spoof mode and did not leak credentials.",
  ],
  troubleshooting: [
    {
      title: "I can’t find the user in Admin search",
      body: [
        "Search by email, then by UID.",
        "If the user should exist but doesn’t show up, escalate with the email/UID and the time you checked.",
      ],
    },
    {
      title: "The spoof session opens but I get a login loop / 401s",
      body: [
        "Close the spoof window and create a new spoof session (tokens can expire).",
        "Use a fresh Incognito/Private window to avoid stale cookies.",
        "If it continues, escalate with the user identifiers, environment, and the approximate time you attempted spoofing.",
      ],
    },
  ],
  escalateWhen: [
    "You do not have explicit customer permission.",
    "You suspect you might be in the wrong org/user.",
    "Spoofing fails repeatedly after a fresh attempt.",
    "The issue involves billing/provider connections/accounting method changes.",
  ],
  relatedGuides: [
    { label: "Submitting a bug report", guideId: "bug-reporting" },
    {
      label: "Investigate a submitted bug report",
      guideId: "bug-report-investigation",
    },
    {
      label: "Billing & Stripe: which link to use",
      guideId: "billing-stripe-which-link",
    },
  ],
  relatedAdminPaths: [
    { label: "View as customer", path: "/spoof" },
    { label: "Customer-view audit", path: "/spoof-sessions" },
    { label: "Users", path: "/users" },
  ],
};
